import React from 'react'
import { isEmpty, length, map } from 'ramda'
import { WorkInProgressIcon } from 'app/components/icons'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CANCEL, CLOSE, SECONDARY } from 'app/constants/button'
import { useIntl, FormattedMessage } from 'react-intl'
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined'
import { CircularProgress, Typography, makeStyles } from '@material-ui/core'
import moment from 'moment'
import { DISPLAY_FORMAT_DATE } from 'libs/dateUtils'
import NotesAction from './NotesAction'
import { Divider } from '@material-ui/core'
import SimpleDialog from 'app/componentV4/Dialog/SimpleDialog'
import clsx from 'clsx'
import {
  closeWarningDialog,
  openWarningDialog,
} from 'app/store/actions/warningDialog.actions'
import { useDispatch } from 'react-redux'

const MAX_CHARACTER = 300

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '350px',
      maxWidth: '500px',
      maxHeight: '500px',
      overflowX: 'auto',
      padding: '2.5rem',
    },
    '& .MuiPaper-root': {
      padding: '10px !important',
    },
    '& .MuiButtonBase-root': {
      padding: '5px !important',
    },
  },
  button: {
    '& .MuiButtonBase-root': {
      borderRadius: '4px !important',
      border: '1px solid var(--Primary-300, #8D8D8D) !important',
      lineHeight: 'normal !important',
      color: '#8D8D8D',
    },
  },
  noteSection: {
    maxHeight: '400px !important',
  },
})

export default ({
  subscriber,
  notes,
  isLoading,
  onOpenFormDialog,
  onDeleteNote,
  noteDetail,
  openView,
  onOpenViewNote,
  closeViewNote,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()

  const handleDeleteNote = ({ noteId }) => {
    const payload = {
      title: formatMessage({ id: 'note.delete.title' }),
      description: formatMessage({
        id: 'subscriberDetail.notes.delete.confirm.text',
      }),
      submitText: formatMessage({
        id: 'action.delete',
      }),
      closeText: formatMessage({ id: 'action.cancel' }),
      type: CANCEL,
      onSubmit: () => {
        onDeleteNote({ noteId })
        dispatch(closeWarningDialog())
      },
      onClose: () => {
        dispatch(closeWarningDialog())
      },
    }
    dispatch(openWarningDialog(payload))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-216">
        <CircularProgress className="self-center m-16" />
      </div>
    )
  }

  return (
    <div className="w-full">
      {isEmpty(notes) ? (
        <div className="flex text-center w-full justify-center text-16 mb-10">
          <div className="space-y-5">
            <div>
              <WorkInProgressIcon />
            </div>
            <div className="sm:w-400">
              <FormattedMessage
                id="subscriberDetail.notes.empty.label"
                values={{
                  b: (text) => <b>{text}</b>,
                  name: `${subscriber.firstName} ${subscriber.lastName}`,
                }}
              />
            </div>
            <div>
              <ContainedButton
                color={SECONDARY}
                onClick={() => onOpenFormDialog()}
                type="button"
              >
                <div className="flex space-x-4 font-normal text-16 justify-center items-center">
                  <AddBoxOutlinedIcon fontSize="small" />
                  <span className="normal-case">
                    {formatMessage({
                      id: 'subscriberDetail.notes.add',
                    })}
                  </span>
                </div>
              </ContainedButton>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            'space-y-5 text-14 text-blue-950 overflow-y-auto w-full',
            classes.noteSection,
          )}
        >
          {map((note) => {
            return (
              <div
                className="flex w-full rounded-4 border border-primary-350 p-5 text-14 cursor-pointer"
                onClick={() => onOpenViewNote(note)}
              >
                <div className="flex-1 space-y-5">
                  <div>
                    {length(note.text) > MAX_CHARACTER
                      ? note.text.slice(0, MAX_CHARACTER) + '...'
                      : note.text}
                  </div>
                  <div className="flex space-x-5 text-primary-300 text-14">
                    <div>
                      <FormattedMessage
                        id="created.by.label"
                        values={{
                          fullName: note.createdBy.name,
                        }}
                      />
                    </div>
                    <Divider orientation="vertical" flexItem />
                    <div>
                      {moment(note.updatedAt).format(DISPLAY_FORMAT_DATE)}
                    </div>
                  </div>
                </div>
                <div className="px-2 sm:px-5">
                  <NotesAction
                    note={note}
                    onEdit={() => onOpenFormDialog(note)}
                    onDelete={() => handleDeleteNote({ noteId: note.id })}
                  />
                </div>
              </div>
            )
          }, notes)}
        </div>
      )}
      <SimpleDialog
        id="motes-detail-dailog"
        open={openView}
        title={
          <div className="my-auto">
            <Typography className="text-14 font-bold text-blue-950 px-5">
              {formatMessage({
                id: 'note.details.title',
              })}
            </Typography>
          </div>
        }
        onClose={closeViewNote}
        className={classes.root}
      >
        <div className="p-5 space-y-5">
          <div className="bg-primary-200 p-5 rounded-8">
            <div className="flex space-x-5 text-primary-300 text-14">
              <div>
                <FormattedMessage
                  id="created.by.label"
                  values={{
                    fullName: `${noteDetail?.subscriber?.firstName} ${noteDetail?.subscriber?.lastName}`,
                  }}
                />
              </div>
              <Divider orientation="vertical" flexItem />
              <div>
                {moment(noteDetail?.updatedAt).format(DISPLAY_FORMAT_DATE)}
              </div>
            </div>
          </div>
          <Typography className="text-14 text-grey-400">
            {noteDetail?.text}
          </Typography>
          <div className={clsx(classes.button, 'text-center my-10')}>
            <ContainedButton
              color={CLOSE}
              onClick={closeViewNote}
              type="button"
            >
              <div className="flex font-normal text-14 justify-center items-center">
                <span className="capitalize">
                  {formatMessage({
                    id: 'text.close',
                  })}
                </span>
              </div>
            </ContainedButton>
          </div>
        </div>
      </SimpleDialog>
    </div>
  )
}
